import {
    ChevronDown,
    X as CloseIcon,
    LogOut,
    Menu as MenuIcon,
    UserRound,
    Users,
} from "lucide-react";
import Link from "next/link";
import { useRouter } from "next/router";
import React, { useEffect, useMemo, useRef, useState } from "react";

import { Logo } from "@/components/Logo";
import { app } from "@/lib/app";
import { appInfo } from "@/lib/app-info";
import { useAccount, useCurrentUser, useFileUrl, useIsInFullPageMode, useIsNoMarginMode, useUserInfo } from "@/lib/hooks";
import { Account, AccountMembership, typeDefs, User } from "@/lib/schema";
import { store } from "@/lib/store";
import { supClient } from "@/lib/supabase";
import { cn } from "@/lib/util";
import { useElementSize } from "@iyio/react-common";
import { AccessCodeView } from "./AccessCodeView";
import { Globe } from "./Globe";

export interface MainLayoutProps {
    children: React.ReactNode;
}

interface NavItem {
    label: string;
    href: string;
}

const NAV_ITEMS: NavItem[] = [
    { label: "Home", href: "/" },
    { label: "Guardians", href: "/guardians" },
    { label: "Vision", href: "/about-us" },
];

function useUserMembershipAccounts(user: User | undefined): { memberships: AccountMembership[]; accounts: Account[]; loading: boolean } {
    const [loading, setLoading] = useState<boolean>(false);
    const [memberships, setMemberships] = useState<AccountMembership[]>([]);
    const [accounts, setAccounts] = useState<Account[]>([]);

    useEffect(() => {
        let cancelled = false;
        async function loadAsync() {
            if (!user) {
                setMemberships([]);
                setAccounts([]);
                setLoading(false);
                return;
            }
            setLoading(true);
            try {
                const ms = (await store().selectMatchesAsync<AccountMembership>(
                    typeDefs.AccountMembership,
                    { user_id: user.id },
                    { orderBy: "created_at" }
                )) as AccountMembership[];

                const accountIds = Array.from(new Set(ms.map(m => m.account_id)));
                const fetched: Account[] = [];
                for (const accountId of accountIds) {
                    const acc = (await store().selectFirstAsync<Account>(typeDefs.Account, accountId)) as Account | undefined;
                    if (acc) fetched.push(acc);
                }
                if (!cancelled) {
                    setMemberships(ms);
                    setAccounts(fetched);
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        }
        loadAsync();
        return () => {
            cancelled = true;
        };
    }, [user]);

    return { memberships, accounts, loading };
}

function Avatar({ user }: { user: User | undefined }) {
    const url = useFileUrl(user?.profile_image_path);
    const initials = useMemo(() => {
        if (!user?.name) return "?";
        const parts = user.name.trim().split(/\s+/);
        return parts.slice(0, 2).map(p => p[0]?.toUpperCase()).join("");
    }, [user?.name]);

    return (
        <div className="relative inline-flex items-center justify-center size-8 rounded-full bg-[rgba(255,255,255,0.06)] border border-[rgba(255,255,255,0.08)] overflow-hidden">
            {url === null ? (
                <div className="animate-pulse w-full h-full bg-[rgba(255,255,255,0.04)]" />
            ) : url ? (
                // eslint-disable-next-line @next/next/no-img-element
                <img src={url} alt={user?.name ?? "User"} className="w-full h-full object-cover" />
            ) : (
                <span className="text-xs text-[rgba(247,244,239,0.85)]">{initials}</span>
            )}
        </div>
    );
}

function AccountSwitcher() {
    const userInfo = useUserInfo();
    const user = userInfo === null ? undefined : userInfo?.user;
    const currentAccount = useAccount();
    const [open, setOpen] = useState(false);
    const wrapperRef = useRef<HTMLDivElement | null>(null);
    const { accounts, loading } = useUserMembershipAccounts(user);

    const currentLabel = currentAccount?.name ?? "No account";

    async function onSwitchAsync(accountId: string) {
        await app().switchAccountAsync(accountId);
        setOpen(false);
    }

    useEffect(() => {
        function handlePointerDown(e: MouseEvent | PointerEvent) {
            if (!open) return;
            if (!wrapperRef.current) return;
            const target = e.target as Node | null;
            if (target && !wrapperRef.current.contains(target)) {
                setOpen(false);
            }
        }
        function handleKey(e: KeyboardEvent) {
            if (e.key === "Escape") setOpen(false);
        }
        document.addEventListener("pointerdown", handlePointerDown);
        document.addEventListener("keydown", handleKey);
        return () => {
            document.removeEventListener("pointerdown", handlePointerDown);
            document.removeEventListener("keydown", handleKey);
        };
    }, [open]);

    return (
        <div className="relative" ref={wrapperRef}>
            <button
                className="btn btn--secondary inline-flex items-center gap-2"
                onClick={() => setOpen(o => !o)}
                aria-haspopup="menu"
                aria-expanded={open}
                aria-controls="account-switcher-menu"
            >
                <Users className="size-4 icon-muted" aria-hidden="true" />
                <span className="max-w-40 truncate">{currentLabel}</span>
                <ChevronDown className="size-4 icon-muted" aria-hidden="true" />
            </button>

            {open && (
                <div
                    id="account-switcher-menu"
                    role="menu"
                    className="absolute right-0 mt-2 min-w-56 glass p-2 rounded-minor shadow-glass z-[100]"
                >
                    <div className="px-2 py-1 text-xs text-subtle">Switch organization</div>
                    <div className="max-h-64 overflow-auto stack-gap">
                        {loading && <div className="px-2 py-2 text-muted text-sm">Loading…</div>}
                        {!loading && accounts.length === 0 && (
                            <div className="px-2 py-2 text-muted text-sm">No organizations found</div>
                        )}
                        {!loading &&
                            accounts.map(acc => (
                                <button
                                    key={acc.id}
                                    onClick={() => onSwitchAsync(acc.id)}
                                    className={cn(
                                        "w-full text-left px-3 py-2 rounded-minor card-actionable",
                                        acc.id === currentAccount?.id ? "jade" : "text-[rgba(247,244,239,0.85)]"
                                    )}
                                    role="menuitem"
                                >
                                    {acc.name}
                                </button>
                            ))}
                    </div>
                    <div className="border-t border-[rgba(255,255,255,0.06)] mt-2 pt-2">
                        <Link href="/account" role="menuitem" className="block px-3 py-2 text-sm card-actionable rounded-minor">
                            Manage organizations
                        </Link>
                    </div>
                </div>
            )}
        </div>
    );
}

function UserMenu() {
    const userInfo = useUserInfo();
    const user = userInfo === null ? undefined : userInfo?.user;
    const [open, setOpen] = useState(false);
    const wrapperRef = useRef<HTMLDivElement | null>(null);

    async function signOutAsync() {
        await supClient().auth.signOut();
        setOpen(false);
    }

    useEffect(() => {
        function handlePointerDown(e: MouseEvent | PointerEvent) {
            if (!open) return;
            if (!wrapperRef.current) return;
            const target = e.target as Node | null;
            if (target && !wrapperRef.current.contains(target)) {
                setOpen(false);
            }
        }
        function handleKey(e: KeyboardEvent) {
            if (e.key === "Escape") setOpen(false);
        }
        document.addEventListener("pointerdown", handlePointerDown);
        document.addEventListener("keydown", handleKey);
        return () => {
            document.removeEventListener("pointerdown", handlePointerDown);
            document.removeEventListener("keydown", handleKey);
        };
    }, [open]);

    return (
        <div className="relative" ref={wrapperRef}>
            <button
                className="inline-flex items-center gap-2 px-1"
                onClick={() => setOpen(o => !o)}
                aria-haspopup="menu"
                aria-expanded={open}
                aria-controls="user-menu"
            >
                <Avatar user={user} />
                <ChevronDown className="size-4 icon-muted" aria-hidden="true" />
                <span className="sr-only">Open user menu</span>
            </button>

            {open && (
                <div id="user-menu" className="absolute right-0 mt-2 min-w-48 glass p-2 rounded-minor shadow-glass z-[100]" role="menu">
                    <Link href="/profile" className="flex items-center gap-2 px-3 py-2 rounded-minor card-actionable" role="menuitem">
                        <UserRound className="size-4 icon-muted" aria-hidden="true" />
                        <span>Profile</span>
                    </Link>
                    <Link href="/account" className="flex items-center gap-2 px-3 py-2 rounded-minor card-actionable" role="menuitem">
                        <Users className="size-4 icon-muted" aria-hidden="true" />
                        <span>Organization</span>
                    </Link>
                    <button onClick={signOutAsync} className="w-full flex items-center gap-2 px-3 py-2 rounded-minor card-actionable" role="menuitem">
                        <LogOut className="size-4 icon-muted" aria-hidden="true" />
                        <span>Sign out</span>
                    </button>
                </div>
            )}
        </div>
    );
}

function DesktopNavLinks() {
    const router = useRouter();
    const pathname = router.pathname;

    function isActive(href: string) {
        if (href === "/") return pathname === "/";
        return pathname.startsWith(href);
    }

    return (
        <div className="hidden md:flex items-center gap-2">
            {NAV_ITEMS.map(item => {
                const classBase = "px-3 py-2 rounded-minor card-actionable";
                return (
                    <Link
                        key={item.href}
                        href={item.href}
                        className={cn(classBase, isActive(item.href) ? "jade" : "text-[rgba(247,244,239,0.85)]")}
                    >
                        {item.label}
                    </Link>
                );
            })}
        </div>
    );
}

function MobileNavPanel({ isSignedIn, onClose }: { isSignedIn: boolean; onClose: () => void }) {
    return (
        <div className="md:hidden glass p-3 mt-2 rounded-minor shadow-glass stack-gap z-[60]" role="menu" aria-label="Mobile navigation">
            <div className="flex flex-col gap-1">
                {NAV_ITEMS.map(item => (
                    <Link key={item.href} href={item.href} className="px-3 py-2 rounded-minor card-actionable" role="menuitem" onClick={onClose}>
                        {item.label}
                    </Link>
                ))}
            </div>

            <div className="flex items-center justify-between pt-2">
                <span className="text-xs text-subtle">Organization</span>
                {isSignedIn ? <AccountSwitcher /> : <span className="text-subtle text-xs">Not signed in</span>}
            </div>

            {isSignedIn && (
                <div className="pt-1">
                    <UserMenu />
                </div>
            )}
        </div>
    );
}

export function MainLayout({ children }: MainLayoutProps) {
    const isFullPage = useIsInFullPageMode();
    const isNoMargins = useIsNoMarginMode();
    const user = useCurrentUser();
    const isSignedIn = user !== undefined && user !== null;

    const [mobileOpen, setMobileOpen] = useState(false);

    const router = useRouter();
    useEffect(() => {
        const handleRoute = () => setMobileOpen(false);
        router.events.on("routeChangeStart", handleRoute);
        return () => {
            router.events.off("routeChangeStart", handleRoute);
        };
    }, [router.events]);

    const [size,setSizeElem]=useElementSize();

    const [locked,setLocked]=useState(appInfo.comingSoon);

    return (
        <div className={cn("page-shell min-h-screen flex flex-col", isNoMargins && "no-margins")}>
            <Globe/>
            <div className="gc-ambient" />
            <div id="globe-root" aria-hidden="true" />
            
            {!isFullPage && (
                <header className={cn("container-padded relative z-[50]",locked && 'opacity-25 pointer-events-none')}>
                    <nav className="main-centered py-3">
                        <a href="#main-content" className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 btn btn--secondary">
                            Skip to content
                        </a>
                        <div className="flex items-center justify-between gap-3">
                            <Link className="flex items-center gap-2 jade font-semibold" href="/" aria-label="Go to home">
                                <Logo size={18} className="opacity-90" />
                                <span className="hidden sm:inline">Guardians Collective</span>
                            </Link>
                            
                            <DesktopNavLinks />

                            <div className="hidden md:flex items-center gap-2">
                                {isSignedIn && <AccountSwitcher />}
                                {isSignedIn && <UserMenu />}
                            </div>

                            <button
                                className="md:hidden btn btn--secondary px-3"
                                aria-label="Open menu"
                                aria-controls="mobile-nav-panel"
                                aria-expanded={mobileOpen}
                                onClick={() => setMobileOpen(o => !o)}
                            >
                                {mobileOpen ? <CloseIcon className="size-5" /> : <MenuIcon className="size-5" />}
                            </button>
                            
                        </div>

                        {mobileOpen && (
                            <div id="mobile-nav-panel" className="relative z-[60]">
                                <MobileNavPanel isSignedIn={!!isSignedIn} onClose={() => setMobileOpen(false)} />
                            </div>
                        )}
                    </nav>
                </header>
            )}

            <main
                ref={setSizeElem}
                id="main-content"
                className={cn(
                    "flex-1",
                    isNoMargins ? "" : "main-centered container-padded",
                )}
            >
                {locked?<AccessCodeView onPassed={()=>setLocked(false)}/>:children}
            </main>

            {/* <div className="fixed right-0 top-0 h-full pointer-events-none" style={{width:(globalThis.window?.outerWidth??0)-size.width}}>
                <div className="w-full h-full flex flex-col justify-center items-center">
                    <div className="pointer-events-auto">
                        LOGO
                    </div>
                </div>
            </div> */}

            {!isFullPage && (
                <footer className="container-padded py-8 text-subtle">
                    <div className="main-centered text-sm">© Guardians Collective</div>
                </footer>
            )}
        </div>
    );
}